import { useState, useEffect } from "react";
import "./LoginPage.css";

// view: "login" | "signup" | "verify-sent" | "unverified" | "verifying" | "forgot" | "reset"
export default function LoginPage({ onLogin }) {
  const [view, setView]               = useState("login");
  const [email, setEmail]             = useState("");
  const [password, setPassword]       = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetToken, setResetToken]   = useState("");
  const [error, setError]       = useState(null);
  const [info, setInfo]         = useState(null);
  const [loading, setLoading]   = useState(false);

  // Auto-detect tokens from email links
  useEffect(() => {
    const params      = new URLSearchParams(window.location.search);
    const resetTok    = params.get("token");
    const verifyTok   = params.get("verify");
    window.history.replaceState({}, "", window.location.pathname);

    if (resetTok) {
      setResetToken(resetTok);
      setView("reset");
    } else if (verifyTok) {
      setView("verifying");
      fetch(`/api/auth/verify/${verifyTok}`, { method: "POST" })
        .then(r => r.json())
        .then(data => {
          if (data.ok) { setInfo("Email verified! You can now log in."); setView("login"); }
          else { setError(data.error || "Verification failed."); setView("login"); }
        })
        .catch(() => { setError("Verification failed. Please try again."); setView("login"); });
    }
  }, []);

  async function handleLogin(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res  = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
        credentials: "include",
      });
      const data = await res.json();
      if (data.unverified) { setEmail(email); setView("unverified"); return; }
      if (!res.ok) return setError(data.error || "Login failed");
      onLogin(data.user);
    } catch {
      setError("Could not connect to server");
    } finally {
      setLoading(false);
    }
  }

  async function handleRegister(e) {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) return setError("Passwords don't match");
    if (password.length < 8) return setError("Password must be at least 8 characters");
    setLoading(true);
    try {
      const res  = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || "Registration failed");
      setView("verify-sent");
    } catch {
      setError("Could not connect to server");
    } finally {
      setLoading(false);
    }
  }

  async function handleForgot(e) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      const res  = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || "Request failed");
      setInfo(data.message);
    } catch {
      setError("Could not connect to server");
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(e) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (newPassword.length < 8) return setError("Password must be at least 8 characters");
    setLoading(true);
    try {
      const res  = await fetch(`/api/auth/reset-password/${resetToken}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: newPassword }),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || "Reset failed");
      setInfo("Password updated! You can now log in.");
      setView("login");
      setPassword("");
      setNewPassword("");
      setResetToken("");
    } catch {
      setError("Could not connect to server");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-bg">
      <div className="login-card">
        <div className="login-logo">
          <span className="login-logo-icon">✓</span>
          <span className="login-logo-text">WritePushDo</span>
        </div>

        {view === "login" && (
          <>
            <h1 className="login-title">Welcome back</h1>
            <p className="login-sub">Sign in to your tasks</p>
            <form className="login-form" onSubmit={handleLogin}>
              <label className="login-label">Email</label>
              <input
                className="login-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoFocus
                required
              />
              <label className="login-label">Password</label>
              <input
                className="login-input"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
              {error && <div className="login-error">{error}</div>}
              {info  && <div className="login-info">{info}</div>}
              <button className="login-btn" type="submit" disabled={loading}>
                {loading ? "Signing in…" : "Sign in"}
              </button>
            </form>
            <button className="login-link" onClick={() => { setView("forgot"); setError(null); setInfo(null); }}>
              Forgot password?
            </button>
            <button className="login-link" onClick={() => { setView("signup"); setError(null); setInfo(null); }}>
              Don't have an account? Sign up
            </button>
          </>
        )}

        {view === "signup" && (
          <>
            <h1 className="login-title">Create account</h1>
            <p className="login-sub">Sign up for WritePushDo</p>
            <form className="login-form" onSubmit={handleRegister}>
              <label className="login-label">Email</label>
              <input
                className="login-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoFocus
                required
              />
              <label className="login-label">Password</label>
              <input
                className="login-input"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
              />
              <label className="login-label">Confirm password</label>
              <input
                className="login-input"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
              {error && <div className="login-error">{error}</div>}
              <button className="login-btn" type="submit" disabled={loading}>
                {loading ? "Creating account…" : "Create account"}
              </button>
            </form>
            <button className="login-link" onClick={() => { setView("login"); setError(null); setInfo(null); }}>
              ← Back to sign in
            </button>
          </>
        )}

        {view === "verifying" && (
          <>
            <h1 className="login-title">Verifying…</h1>
            <p className="login-sub">Please wait while we verify your email.</p>
            <div className="login-spinner" />
          </>
        )}

        {view === "verify-sent" && (
          <>
            <h1 className="login-title">Check your email</h1>
            <p className="login-sub">We sent a verification link to your email address. Click it to activate your account.</p>
            <p className="login-info-text">Didn't receive it?</p>
            <button className="login-btn" onClick={async () => {
              await fetch("/api/auth/resend-verification", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
              });
              setInfo("Verification email resent!");
            }}>Resend email</button>
            {info && <div className="login-info">{info}</div>}
            <button className="login-link" onClick={() => { setView("login"); setError(null); setInfo(null); }}>
              ← Back to sign in
            </button>
          </>
        )}

        {view === "unverified" && (
          <>
            <h1 className="login-title">Verify your email</h1>
            <p className="login-sub">You need to verify your email before logging in.</p>
            <button className="login-btn" onClick={async () => {
              setLoading(true);
              await fetch("/api/auth/resend-verification", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
              });
              setLoading(false);
              setInfo("Verification email sent! Check your inbox.");
            }} disabled={loading}>
              {loading ? "Sending…" : "Resend verification email"}
            </button>
            {info && <div className="login-info">{info}</div>}
            <button className="login-link" onClick={() => { setView("login"); setError(null); setInfo(null); }}>
              ← Back to sign in
            </button>
          </>
        )}

        {view === "forgot" && (
          <>
            <h1 className="login-title">Reset password</h1>
            <p className="login-sub">We'll send a reset link to your email</p>
            <form className="login-form" onSubmit={handleForgot}>
              <label className="login-label">Email</label>
              <input
                className="login-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoFocus
                required
              />
              {error && <div className="login-error">{error}</div>}
              {info  && <div className="login-info">{info}</div>}
              <button className="login-btn" type="submit" disabled={loading}>
                {loading ? "Sending…" : "Send reset link"}
              </button>
            </form>
            <button className="login-link" onClick={() => { setView("login"); setError(null); setInfo(null); }}>
              ← Back to sign in
            </button>
          </>
        )}

        {view === "reset" && (
          <>
            <h1 className="login-title">Set new password</h1>
            <p className="login-sub">Enter your new password below</p>
            <form className="login-form" onSubmit={handleReset}>
              <input type="hidden" value={resetToken} />
              <label className="login-label">New password</label>
              <input
                className="login-input"
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
              />
              {error && <div className="login-error">{error}</div>}
              {info  && <div className="login-info">{info}</div>}
              <button className="login-btn" type="submit" disabled={loading}>
                {loading ? "Updating…" : "Set new password"}
              </button>
            </form>
            <button className="login-link" onClick={() => { setView("login"); setError(null); setInfo(null); }}>
              ← Back to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}
